import { useEffect, useMemo, useState, type ComponentType } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
  IconHandFinger, IconBookmark, IconBook, IconSend, IconCheck,
  IconBriefcase, IconActivity, IconUsersGroup, IconCoin, IconBulbFilled,
  IconClock, IconBrain, IconHeartHandshake, IconTrendingUp, IconShield,
} from '@tabler/icons-react';

// id из backend/database/seeders/data/topic_*.json
const TOPIC_ICON_BY_ID: Record<string, ComponentType<{ size?: number | string }>> = {
  bus: IconBriefcase,
  hlt: IconActivity,
  ldr: IconUsersGroup,
  mon: IconCoin,
  phi: IconBulbFilled,
  prod: IconClock,
  psy: IconBrain,
  rel: IconHeartHandshake,
  slf: IconTrendingUp,
  sto: IconShield,
};
import RU from 'country-flag-icons/react/3x2/RU';
import GB from 'country-flag-icons/react/3x2/GB';
import ES from 'country-flag-icons/react/3x2/ES';
import DE from 'country-flag-icons/react/3x2/DE';
import FR from 'country-flag-icons/react/3x2/FR';
import { Button } from '@/components/Button';
import { ProgressBar } from '@/components/ProgressBar';
import { api } from '@/api/endpoints';
import { useAuth } from '@/store/auth';
import type { Topic, Lang } from '@/api/types';
import i18n from '@/i18n';

const LANGS: { code: Lang; Flag: typeof RU; label: string }[] = [
  { code: 'ru', Flag: RU, label: 'Русский' },
  { code: 'en', Flag: GB, label: 'English' },
  { code: 'es', Flag: ES, label: 'Español' },
  { code: 'de', Flag: DE, label: 'Deutsch' },
  { code: 'fr', Flag: FR, label: 'Français' },
];

export default function Onboarding() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const user = useAuth(s => s.user);
  const patchUser = useAuth(s => s.patchUser);

  const [step, setStep] = useState(0);
  const [lang, setLang] = useState<Lang>(user?.lang ?? 'en');
  const [topics, setTopics] = useState<string[]>(user?.topics ?? []);
  const [allTopics, setAllTopics] = useState<Topic[]>([]);

  // Рефетчим список тем при смене user.lang (после setLang на шаге 0):
  // backend EnsureValidLocale предпочитает user->lang, так что новые имена
  // приходят на выбранном языке. До фикса топики оставались на исходном
  // языке Telegram до перезапуска приложения.
  useEffect(() => {
    void api.topics.list().then(({ data }) => setAllTopics(data));
  }, [user?.lang]);

  // Применяем выбранный язык в i18n on-the-fly.
  useEffect(() => { void i18n.changeLanguage(lang); }, [lang]);

  const total = 5;
  const canSkip = step !== total - 1;

  const next = async () => {
    if (step === 0) {
      await api.me.setLang(lang);
      patchUser({ lang });
      setStep(1);
      return;
    }
    if (step < 3) {
      setStep(step + 1);
      return;
    }
    if (step === 3) {
      // сохраняем темы
      if (topics.length < 3 || topics.length > 5) return;
      await api.me.setTopics(topics);
      patchUser({ topics });
      setStep(4);
      return;
    }
    nav('/', { replace: true });
  };

  return (
    <div
      className="h-dvh flex flex-col bg-bg min-h-0"
      style={{
        paddingTop: 'calc(var(--tg-safe-area-inset-top, env(safe-area-inset-top, 0px)) + var(--tg-content-safe-area-inset-top, 0px))',
        paddingBottom: 'calc(var(--tg-safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)) + var(--tg-content-safe-area-inset-bottom, 0px))',
        paddingLeft: 'calc(var(--tg-safe-area-inset-left, env(safe-area-inset-left, 0px)) + var(--tg-content-safe-area-inset-left, 0px))',
        paddingRight: 'calc(var(--tg-safe-area-inset-right, env(safe-area-inset-right, 0px)) + var(--tg-content-safe-area-inset-right, 0px))',
      }}
    >
      <header className="px-4 pt-4 flex items-center gap-3">
        <ProgressBar value={(step + 1) / total} className="flex-1" />
        {canSkip && (
          <button onClick={() => setStep(3)} className="text-xs text-text-muted">
            {t('common.skip')}
          </button>
        )}
      </header>

      <main className="flex-1 min-h-0 overflow-y-auto px-6 py-6">
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25 }}
            className="h-full"
          >
            {step === 0 && (
              <StepLang value={lang} onChange={setLang} />
            )}
            {step === 1 && (
              <StepWelcome />
            )}
            {step === 2 && (
              <StepHow />
            )}
            {step === 3 && (
              <StepTopics all={allTopics} value={topics} onChange={setTopics} />
            )}
            {step === 4 && (
              <StepDone topics={allTopics.filter(tp => topics.includes(tp.id))} />
            )}
          </motion.div>
        </AnimatePresence>
      </main>

      <footer className="p-4 safe-bottom">
        <Button block size="lg" onClick={next}
          disabled={step === 3 && (topics.length < 3 || topics.length > 5)}
        >
          {step === 4 ? t('onboarding.done_cta') : t('common.next')}
        </Button>
      </footer>
    </div>
  );
}

/* ----------- steps ----------- */

function StepLang({ value, onChange }: { value: Lang; onChange: (l: Lang) => void }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-6">
      <h1 className="font-content text-3xl">{t('onboarding.lang_title')}</h1>
      <div className="space-y-2">
        {LANGS.map(l => (
          <button
            key={l.code}
            onClick={() => onChange(l.code)}
            className={[
              'w-full h-14 px-4 flex items-center gap-3 rounded-xl border transition',
              value === l.code ? 'bg-white border-text' : 'bg-surface border-transparent hover:border-text/20',
            ].join(' ')}
          >
            <l.Flag title={l.label} className="w-7 h-[18px] rounded-sm shrink-0" />
            <span className="text-base font-medium">{l.label}</span>
            {value === l.code && <span className="ml-auto text-base">✓</span>}
          </button>
        ))}
      </div>
    </div>
  );
}

function StepWelcome() {
  const { t } = useTranslation();
  return (
    <div className="h-full flex flex-col gap-4">
      <div>
        <p className="text-xs tracking-widest font-semibold text-text-muted">{t('onboarding.welcome_eyebrow')}</p>
        <h1 className="font-content text-[30px] leading-tight mt-3">{t('onboarding.welcome_title')}</h1>
        <p className="text-text-muted mt-3">{t('onboarding.welcome_sub')}</p>
      </div>
      <img
        src="/onboarding-welcome.webp"
        alt=""
        aria-hidden
        loading="eager"
        decoding="async"
        className="mt-2 w-full max-h-[55vh] object-cover rounded-2xl select-none pointer-events-none"
      />
    </div>
  );
}

function StepHow() {
  const { t } = useTranslation();
  const items = [
    { icon: IconHandFinger, label: t('onboarding.how_swipe') },
    { icon: IconBookmark,   label: t('onboarding.how_save') },
    { icon: IconBook,       label: t('onboarding.how_book') },
    { icon: IconSend,       label: t('onboarding.how_share') },
  ];
  return (
    <div className="space-y-6">
      <h1 className="font-content text-2xl">{t('onboarding.how_title')}</h1>
      <ul className="space-y-3">
        {items.map((it, i) => (
          <li key={i} className="flex items-start gap-3 bg-surface p-3 rounded-xl">
            <div className="w-[42px] h-[42px] rounded-md bg-white/70 grid place-items-center text-text">
              <it.icon size={22} stroke={2} />
            </div>
            <p className="flex-1 pt-2 text-sm">{it.label}</p>
          </li>
        ))}
      </ul>
    </div>
  );
}

function StepTopics({ all, value, onChange }: { all: Topic[]; value: string[]; onChange: (v: string[]) => void }) {
  const { t } = useTranslation();
  const status = useMemo(() => {
    const n = value.length;
    if (n < 3) return t('onboarding.topics_count_min', { n });
    if (n > 5) return t('onboarding.topics_count_max');
    return t('onboarding.topics_count_ok', { n });
  }, [value.length, t]);

  const toggle = (id: string) => {
    onChange(value.includes(id) ? value.filter(x => x !== id) : [...value, id]);
  };

  return (
    <div className="space-y-4">
      <h1 className="font-content text-2xl">{t('onboarding.topics_title')}</h1>
      <p className="text-text-muted text-sm">{t('onboarding.topics_hint')}</p>
      <div className="space-y-2">
        {all.map(tp => {
          const active = value.includes(tp.id);
          const Icon = TOPIC_ICON_BY_ID[tp.id] ?? IconBookmark;
          return (
            <button
              key={tp.id}
              onClick={() => toggle(tp.id)}
              className={[
                'w-full p-3 rounded-xl text-left flex items-center gap-3 transition',
                active ? 'bg-accent text-text-inverse' : 'bg-surface text-text',
              ].join(' ')}
            >
              <div className={[
                'w-10 h-10 rounded-md grid place-items-center shrink-0',
                active ? 'bg-white/15' : 'bg-white/60',
              ].join(' ')}>
                <Icon size={22} />
              </div>
              <span className="flex-1 font-content text-base leading-snug">{tp.name}</span>
              <span className="text-base shrink-0 w-5 text-center">{active ? '✓' : ''}</span>
            </button>
          );
        })}
      </div>
      <p className="text-center text-xs text-text-muted">{status}</p>
    </div>
  );
}

function StepDone({ topics }: { topics: Topic[] }) {
  const { t } = useTranslation();
  return (
    <div className="h-full flex flex-col items-center justify-center text-center space-y-5">
      <div className="w-20 h-20 rounded-pill bg-success grid place-items-center">
        <IconCheck size={40} stroke={3} className="text-white" />
      </div>
      <h1 className="font-content text-2xl">{t('onboarding.done_title')}</h1>
      <div className="flex flex-wrap justify-center gap-2 max-w-xs">
        {topics.map(tp => (
          <span key={tp.id} className="px-3 h-8 rounded-pill bg-surface text-sm flex items-center gap-1">
            <i className={`ti ${tp.icon}`} aria-hidden />
            {tp.name}
          </span>
        ))}
      </div>
    </div>
  );
}
